import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CircularProgress, Divider } from "@mui/material";
import SimpleBar from "simplebar-react";

import PrimaryButton from "../../Components/Buttons/primary-button";
import { durationOptions, frequencyOptions } from ".";

import renderCancelRentPayment from "../../Organisms/Modals/Pay Rent/cancel-rent-payment";

import { useModalContext } from "../../Contexts/ModalContext";

import { useRecurringRentPayments, useStakeDebit } from "../../CustomHooks/Queries";

import { getTransformedRentPaymentTransaction } from "../../Utils/api-data-transforms";
import { capitaliseString, formatCurrency, formatDate, getNumberWithOrdinal } from "../../Utils/helpers";
import { longMonthDateFormat } from "../../Utils/constants";

import { ArrowLeftCircle } from "../../Assets/Icons";
import renderRentPaymentDetails from "../../Organisms/Modals/Pay Rent/rent-payment-details";

function RentPaymentDetails() {
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  const subTransactions = useMemo(
    () => getTransformedRentPaymentTransaction(selectedTransaction?.transactions),
    [selectedTransaction]
  );

  const { recurringId } = useParams();

  const navigate = useNavigate();

  const modalContext = useModalContext();

  const { isLoading: isLoadingStakeDebit, data: stakeDebit } = useStakeDebit();

  const { isFetching: isFetchingRecurringRentPayments } = useRecurringRentPayments({
    onSuccess: (transactions) => {
      const transaction = transactions?.find((transaction) => {
        return transaction?.id === parseInt(recurringId);
      });
      if (!!transaction) {
        setSelectedTransaction(transaction);
      } else {
        navigate(-1);
      }
    },
  });

  const isLoading = isLoadingStakeDebit || isFetchingRecurringRentPayments;

  return (
    <div className="rent-payment-details">
      <section className="card-section-wrapper">
        <div className="max-width-container">
          <div className="card-section">
            <div className="section-header">
              <button className="go-back-button" onClick={() => navigate(-1)} aria-label="Go back">
                <ArrowLeftCircle />
              </button>
              <h3 className="section-title">Payment Details</h3>
              <div className="dummy"></div>
            </div>
            {isLoading ? (
              <div className="loading-content">
                <CircularProgress />
              </div>
            ) : (
              <div className="content">
                <div className="top-container pt-2">
                  <div className="rent-payment-card borderless">
                    <div className="payee-card align-items-start">
                      <div className="account-badge">{selectedTransaction?.payeeAcronym}</div>
                      <div className="info-container">
                        <p className="title">{selectedTransaction?.to}</p>
                        <p className="description">{selectedTransaction?.payee}</p>
                      </div>
                      <div className="transfer-info">
                        <div className="transfer-status-badge positive">
                          <selectedTransaction.StatusIcon /> {selectedTransaction?.statusDisplayName}
                        </div>
                      </div>
                    </div>
                    <div className="payment-details">
                      <div className="transfer-detail">
                        <span className="detail-label">Total Amount</span>
                        <span className="detail-value">{formatCurrency(selectedTransaction?.total)}</span>
                      </div>
                      <Divider />
                      <div className="transfer-detail">
                        <span className="detail-label">Amount</span>
                        <span className="detail-value">{formatCurrency(selectedTransaction?.amount)}</span>
                      </div>
                      <div className="transfer-detail">
                        <span className="detail-label">Fee</span>
                        <span className="detail-value">{formatCurrency(selectedTransaction?.fees)}</span>
                      </div>
                      <Divider />
                      {!!selectedTransaction?.notes && (
                        <div className="transfer-detail">
                          <span className="detail-label">Note</span>
                          <span className="detail-value">{selectedTransaction?.notes}</span>
                        </div>
                      )}
                      <div className="transfer-detail">
                        <span className="detail-label">From</span>
                        <span className="detail-value">
                          Stake Checking · {stakeDebit?.data?.data?.account_number?.slice(-4)}
                        </span>
                      </div>
                      <div className="transfer-detail">
                        <span className="detail-label">Scheduled on</span>
                        <span className="detail-value">
                          {formatDate(selectedTransaction?.scheduled_on, longMonthDateFormat)}
                        </span>
                      </div>
                      <div className="transfer-detail">
                        <span className="detail-label">Frequency</span>
                        <span className="detail-value">
                          {capitaliseString(selectedTransaction?.frequency_type)} on{" "}
                          {selectedTransaction?.frequency_type === frequencyOptions[2].value
                            ? `the ${getNumberWithOrdinal(selectedTransaction?.frequency_day)}`
                            : capitaliseString(selectedTransaction?.frequency_day)}
                        </span>
                      </div>
                      {!!selectedTransaction?.remaining_payments && (
                        <div className="transfer-detail">
                          <span className="detail-label">Number of Payments</span>
                          <span className="detail-value">{selectedTransaction?.number_of_payments}</span>
                        </div>
                      )}
                      <div className="transfer-detail">
                        <span className="detail-label">Next Payment Date</span>
                        <span className="detail-value">
                          {formatDate(selectedTransaction?.next_payment_date, longMonthDateFormat)}
                        </span>
                      </div>
                      <div className="transfer-detail">
                        <span className="detail-label">Last Payment Date</span>
                        <span className="detail-value">
                          {selectedTransaction?.duration === durationOptions[0].value
                            ? "Until Canceled"
                            : formatDate(selectedTransaction?.last_payment_date, longMonthDateFormat)}
                        </span>
                      </div>
                    </div>
                  </div>
                  {subTransactions?.length > 0 && (
                    <>
                      <div className="section-header mt-4 w-100">
                        <div className="section-title">Transaction Activity</div>
                      </div>
                      <SimpleBar style={{ maxHeight: 300 }}>
                        {subTransactions?.map((transaction, idx) => (
                          <button
                            key={idx}
                            className="rent-transaction-activity"
                            onClick={renderRentPaymentDetails.bind(this, modalContext, { paymentDetails: transaction })}
                          >
                            <div className="transfer-detail py-0">
                              <span className="transfer-date">
                                {formatDate(transaction?.created_at, longMonthDateFormat)}
                              </span>
                              <span className="transfer-value">{formatCurrency(transaction?.total)}</span>
                            </div>
                            <div className="transfer-detail py-0">
                              <span className="transaction-description">
                                {transaction?.type} payment to {transaction?.payee}
                              </span>
                              <div className={`transfer-status-badge ${transaction.statusType}`}>
                                <transaction.StatusIcon /> {transaction?.statusDisplayName}
                              </div>
                            </div>
                          </button>
                        ))}
                      </SimpleBar>
                    </>
                  )}
                </div>
                <div className="bottom-container">
                  <PrimaryButton
                    className="red-theme"
                    onClick={renderCancelRentPayment.bind(this, modalContext, {
                      isRecurringTransaction: true,
                      ...selectedTransaction,
                    })}
                  >
                    Cancel Payment
                  </PrimaryButton>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

export default RentPaymentDetails;
